(function () {
  'use strict';

  // Reads data.recommendable_families (same key assets/sup-fin-finder.js
  // reads) so this table's "fit" column always agrees with what the finder
  // tool itself would tell the same customer — one source of truth instead
  // of two hardcoded lists that can drift apart.
  function buildSystemToFamily(recommendableFamilies) {
    var map = {};
    Object.keys(recommendableFamilies || {}).forEach(function (family) {
      (recommendableFamilies[family] || []).forEach(function (system) {
        map[system] = family;
      });
    });
    return map;
  }

  function matchState(systemToFamily, brand, model) {
    if (!model || !model.fin_box_system) return 'none';
    if (!systemToFamily[model.fin_box_system]) return 'none';
    var confidence = model.confidence || (brand && brand.default_confidence);
    return confidence === 'confirmed' ? 'exact' : 'likely';
  }

  function BrandFinGuide(root) {
    this.root = root;
    this.databaseUrl = root.dataset.databaseUrl || '';
    this.brandHandle = root.dataset.brandHandle || '';
    this.tableStyle = root.dataset.tableStyle || 'table';

    this.modelCardsContainer = root.querySelector('[data-model-cards]');
    this.quickSummaryGrid = root.querySelector('[data-quick-summary-grid]');
    this.systemsGrid = root.querySelector('[data-systems-grid]');
    this.warningBox = root.querySelector('[data-warning-box]');

    this.strings = {};
    try {
      this.strings = JSON.parse(root.dataset.uiStrings || '{}');
    } catch (e) {}

    this.systemToFamily = {};

    this._loadDatabase();
  }

  BrandFinGuide.prototype._loadDatabase = function () {
    var self = this;
    if (!this.databaseUrl || !this.brandHandle) return;

    fetch(this.databaseUrl)
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        self.systemToFamily = buildSystemToFamily(json.recommendable_families);

        var brand = (json.brands || []).filter(function (b) {
          return b.handle === self.brandHandle;
        })[0];

        if (self.tableStyle === 'cards') {
          self._renderModelCards(brand);
        }
        // else: table_style 'table' rows are already server-rendered by
        // snippets/fin-compat-rows.liquid (generated from the same database
        // by scripts/build-fin-tables.mjs) — crawlers and no-JS visitors get
        // the real table already, so there's nothing left for JS to build.
        self._renderWarning(brand);

        var systems = self._presentSystems(json, brand);
        self._renderQuickSummary(systems);
        self._renderSystemsExplainer(systems, brand);
      })
      .catch(function (err) {
        console.warn('[BrandFinGuide] Could not load fin database', err);
      });
  };

  BrandFinGuide.prototype._systemLabel = function (id) {
    var labels = this.strings.systemLabels || {};
    return labels[id] || (id || '').replace(/_/g, ' ');
  };

  // Note: there's no _renderTable here anymore — table_style 'table' rows
  // are server-rendered by snippets/fin-compat-rows.liquid (see
  // _loadDatabase above), so JS has nothing left to build for that style.
  // _renderModelCards below still owns the 'cards' style, which the
  // generator doesn't produce markup for.

  BrandFinGuide.prototype._renderModelCards = function (brand) {
    if (!this.modelCardsContainer) return;
    this.modelCardsContainer.innerHTML = '';

    var models = (brand && brand.models) || [];
    if (!models.length) {
      var empty = document.createElement('div');
      empty.className = 'bfg__table-loading';
      empty.textContent = this.strings.noModels || 'No models found.';
      this.modelCardsContainer.appendChild(empty);
      return;
    }

    var self = this;
    models.forEach(function (model) {
      var state = matchState(self.systemToFamily, brand, model);
      var badgeLabel =
        state === 'exact'
          ? self.strings.fitExact
          : state === 'likely'
            ? self.strings.fitLikely
            : self.strings.fitNone;

      var card = document.createElement('div');
      card.className = 'bfg__model-card';

      var name = document.createElement('div');
      name.className = 'bfg__model-card-name';
      name.textContent = model.model;
      card.appendChild(name);

      var system = document.createElement('div');
      system.className = 'bfg__model-card-system';
      system.textContent = self._systemLabel(model.fin_box_system);
      card.appendChild(system);

      var footer = document.createElement('div');
      footer.className = 'bfg__model-card-footer';
      var badge = document.createElement('span');
      badge.className = 'bfg__fit-badge' + (state === 'exact' ? ' bfg__fit-badge--exact' : '');
      badge.textContent = badgeLabel || '';
      footer.appendChild(badge);
      card.appendChild(footer);

      self.modelCardsContainer.appendChild(card);
    });
  };

  // Models whose fin_box_system differs from the brand's own default are
  // the "before you order" exceptions — e.g. Red Paddle Co's Ride (fixed
  // iFin) and Compact (Quick-Lock) ranges next to its default US Box.
  BrandFinGuide.prototype._renderWarning = function (brand) {
    if (!this.warningBox) return;

    var defaultSystem = brand && brand.default_fin_box_system;
    var models = (brand && brand.models) || [];
    var diverging = models.filter(function (m) {
      return defaultSystem && m.fin_box_system && m.fin_box_system !== defaultSystem;
    });
    if (!diverging.length) return;

    var headingEl = this.warningBox.querySelector('[data-warning-heading]');
    var introEl = this.warningBox.querySelector('[data-warning-intro]');
    var listEl = this.warningBox.querySelector('[data-warning-list]');

    if (headingEl) headingEl.textContent = this.strings.warningHeading || '';
    if (introEl) introEl.textContent = this.strings.warningIntro || '';
    if (listEl) {
      listEl.innerHTML = '';
      diverging.forEach(function (m) {
        var li = document.createElement('li');
        var strong = document.createElement('strong');
        strong.textContent = m.model;
        li.appendChild(strong);
        var note = m.note || m.notes || '';
        if (note) li.appendChild(document.createTextNode(' — ' + note));
        listEl.appendChild(li);
      });
    }

    this.warningBox.hidden = false;
  };

  // Distinct fin systems actually present among this brand's models, in
  // the canonical order from the database's top-level fin_systems list.
  BrandFinGuide.prototype._presentSystems = function (json, brand) {
    var order = (json && json.fin_systems) || [];
    var models = (brand && brand.models) || [];
    var present = {};
    models.forEach(function (m) {
      if (m.fin_box_system) present[m.fin_box_system] = true;
    });
    return order.filter(function (id) {
      return present[id];
    });
  };

  BrandFinGuide.prototype._renderQuickSummary = function (systems) {
    if (!this.quickSummaryGrid) return;

    var self = this;
    var summary = this.strings.systemSummary || {};
    this.quickSummaryGrid.innerHTML = '';

    systems.forEach(function (id) {
      var card = document.createElement('div');
      card.className = 'bfg__quick-summary-card';

      var title = document.createElement('div');
      title.className = 'bfg__quick-summary-card-title';
      title.textContent = self._systemLabel(id);
      card.appendChild(title);

      var desc = document.createElement('div');
      desc.className = 'bfg__quick-summary-card-desc';
      desc.textContent = summary[id] || '';
      card.appendChild(desc);

      self.quickSummaryGrid.appendChild(card);
    });
  };

  BrandFinGuide.prototype._renderSystemsExplainer = function (systems, brand) {
    if (!this.systemsGrid) return;

    var self = this;
    var identify = this.strings.systemIdentify || {};
    var attach = this.strings.systemAttach || {};
    var products = this.strings.systemProducts || {};
    var models = (brand && brand.models) || [];
    this.systemsGrid.innerHTML = '';

    function row(card, label, value) {
      if (!value) return;
      var el = document.createElement('div');
      el.className = 'bfg__systems-card-row';
      if (label) {
        var strong = document.createElement('strong');
        strong.textContent = label + ' ';
        el.appendChild(strong);
      }
      el.appendChild(document.createTextNode(value));
      card.appendChild(el);
    }

    systems.forEach(function (id) {
      var usedOn = models
        .filter(function (m) {
          return m.fin_box_system === id;
        })
        .map(function (m) {
          return m.model;
        })
        .join(', ');

      var card = document.createElement('div');
      card.className = 'bfg__systems-card';

      var title = document.createElement('div');
      title.className = 'bfg__systems-card-title';
      title.textContent = self._systemLabel(id);
      card.appendChild(title);

      row(card, self.strings.usedOnLabel, usedOn);
      row(card, self.strings.systemIdentifyLabel, identify[id]);
      row(card, self.strings.systemAttachLabel, attach[id]);
      row(card, null, products[id] || self.strings.noProducts);

      self.systemsGrid.appendChild(card);
    });
  };

  document.querySelectorAll('[data-brand-fin-guide-root]').forEach(function (root) {
    new BrandFinGuide(root);
  });
})();
